import { landingRoutes } from "./utils/navigation";
import React from 'react';
import { createBrowserRouter, RouterProvider, Route, Navigate, createRoutesFromElements, Outlet, useParams } from 'react-router-dom';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './App.css';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { VisibilityProvider } from './contexts/VisibilityContext';

// Auth Components
import { Login, Register, InviteRegister, ForcePasswordChangePage } from './components/auth';

// Layout Components
import { ProtectedRoute, Navigation, Footer } from './components/layout';
import RequireVisibilityKey from './components/layout/RequireVisibilityKey';

// Management Components
import { SchoolClassManagement, ClassDetails } from './components/management';
import SubjectManagement from './components/management/SubjectManagement';

// Grade Components
import { GradeRouter } from './components/grades';

// Student Components
import StudentManagement from './components/students/StudentManagement';
import StudentDetail from './components/students/StudentDetail';
import PracticeInstructorStudents from './components/practice-instructor/PracticeInstructorStudents';

// Management Hub
import ManagementHub from './components/management/ManagementHub';
import VisibilitySettingsPage from './components/management/VisibilitySettingsPage';

// Common Components
import ScrollToTop from './components/common/ScrollToTop';
// Other Components
import Dashboard from './components/Dashboard';
import UserList from './components/UserList';
import StudentList from './components/students/StudentList';
import Profile from './components/Profile';
import Changelog from './components/Changelog';
import Contact from './components/layout/Contact';
import InvitationsPage from './components/InvitationsPage';
import OnboardingPage from './components/OnboardingPage';
import VideoLibraryPage from './components/videos/VideoLibraryPage';

// Admin Components
// import LdapAdminPage from './components/admin/LdapAdminPage';
import EmailHistoryPage from './components/admin/EmailHistoryPage';
import AdminLearningDocumentation from './components/admin/AdminLearningDocumentation';
// Learner Components
import LearningDocumentation from './components/LearningDocumentation';

// Evaluation Components
import ExpertAssignmentView from './components/expert/ExpertAssignmentView';
import PlanPilotView from './components/expert/PlanPilotView';

// Bonus Components
import BonusLandingPage from './components/bonus/BonusLandingPage';
import BonusStudentDetailPage from './components/bonus/BonusStudentDetailPage';
import MyBonusPage from './components/bonus/MyBonusPage';

// Semester Review Components
import TrainerSemesterReviewsPage from './components/semesterreview/TrainerSemesterReviewsPage';
import LearnerSemesterReviewsPage from './components/semesterreview/LearnerSemesterReviewsPage';
import SemesterReviewDetailPage from './components/semesterreview/SemesterReviewDetailPage';
import RevisorDashboardPage from './components/revisor/RevisorDashboardPage';
import RevisorReviewsPage from './components/revisor/RevisorReviewsPage';

const RootLayout = () => (
  <>
    <ScrollToTop />
    <div className="App">
      <Outlet />
      <Footer />
    </div>
  </>
);

const LegacyBonusGroupRedirect = () => {
  const { groupSlug } = useParams();
  const map = {
    'it-med': 'IT_MED',
    'kv-fkd-dhf': 'KV_FKD_DHF',
  };
  const group = map[groupSlug] || 'IT_MED';
  return <Navigate to={`/bonus?group=${group}`} replace />;
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootLayout />}>
      {/* Public Routes */}
      <Route path="/login" element={
        <div style={{ flex: 1 }}>
          <Login />
        </div>
      } />
      <Route path="/register" element={
        <div style={{ flex: 1 }}>
          <Register role="LERNENDE" endpoint="/register/apprentice" />
        </div>
      } />
      <Route path="/force-password-change" element={
        <ProtectedRoute>
          <ForcePasswordChangePage />
        </ProtectedRoute>
      } />
      
      {/* Hidden Routes for Instructors */}
      <Route path="/access/7sdF83kLmA" element={
        <div style={{ flex: 1 }}>
          <Register role="BERUFSBILDNER" endpoint="/register/vocational-trainer" />
        </div>
      } />
      <Route path="/access/x92LmsP1Qa" element={
        <div style={{ flex: 1 }}>
          <Register role="PRACTICE_INSTRUCTOR" endpoint="/register/practice-instructor" />
        </div>
      } />

      <Route path="/invite/r/:token" element={
        <div style={{ flex: 1 }}>
          <InviteRegister />
        </div>
      } />

      {/* Protected Routes */}
      <Route path={landingRoutes.DASHBOARD} element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <Dashboard />
          </main>
        </ProtectedRoute>
      } />

      <Route path="/grades" element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <RequireVisibilityKey keys="VIEW_GRADES">
              <GradeRouter />
            </RequireVisibilityKey>
          </main>
        </ProtectedRoute>
      } />

      <Route path={landingRoutes.MANAGEMENT_HUB} element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <ManagementHub />
          </main>
        </ProtectedRoute>
      } />

      <Route path={landingRoutes.APPRENTICE_DIRECTORY} element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <StudentList />
          </main>
        </ProtectedRoute>
      } />

      <Route path="/students/:studentId" element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <RequireVisibilityKey keys="VIEW_ASSIGNED_LEARNER_GRADES">
              <StudentDetail />
            </RequireVisibilityKey>
          </main>
        </ProtectedRoute>
      } />

      <Route path="/students-old" element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <StudentManagement />
          </main>
        </ProtectedRoute>
      } />

      <Route path="/schools" element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <SchoolClassManagement />
          </main>
        </ProtectedRoute>
      } />

      {/* New Semester Review Routes */}
      <Route path={landingRoutes.SEMESTER_REVIEWS} element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <RequireVisibilityKey
              anyKey={[
                "VIEW_PAST_SEMESTER_REVIEWS",
                "CREATE_EDUCATIONAL_REPORTS",
                "VIEW_ASSIGNED_LEARNER_EDUCATIONAL_REPORTS",
                "VIEW_PAST_EDUCATIONAL_REPORTS",
              ]}
            >
              <TrainerSemesterReviewsPage />
            </RequireVisibilityKey>
          </main>
        </ProtectedRoute>
      } />

      <Route path="/practice-instructor/students" element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <PracticeInstructorStudents />
          </main>
        </ProtectedRoute>
      } />

      <Route path="/learner/semester-reviews" element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <RequireVisibilityKey keys="VIEW_SEMESTER_REVIEWS">
              <LearnerSemesterReviewsPage />
            </RequireVisibilityKey>
          </main>
        </ProtectedRoute>
      } />

      <Route path="/semester-review/:id" element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <RequireVisibilityKey anyKey={[
              "VIEW_SEMESTER_REVIEWS",
              "VIEW_PAST_SEMESTER_REVIEWS",
              "VIEW_ASSIGNED_LEARNER_EDUCATIONAL_REPORTS",
              "VIEW_PAST_EDUCATIONAL_REPORTS",
              "CREATE_EDUCATIONAL_REPORTS",
            ]}>
              <SemesterReviewDetailPage />
            </RequireVisibilityKey>
          </main>
        </ProtectedRoute>
      } />

      <Route path="/revisor" element={<Navigate to="/revisor/dashboard" replace />} />

      <Route path="/revisor/dashboard" element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <RevisorDashboardPage />
          </main>
        </ProtectedRoute>
      } />

      <Route path="/revisor/reviews" element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <RevisorReviewsPage />
          </main>
        </ProtectedRoute>
      } />

      <Route path="/learning-documentation" element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <RequireVisibilityKey keys="VIEW_LEARNING_DOCUMENTATION">
              <LearningDocumentation />
            </RequireVisibilityKey>
          </main>
        </ProtectedRoute>
      } />

      <Route path="/videos" element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <VideoLibraryPage />
          </main>
        </ProtectedRoute>
      } />

      <Route path="/users" element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <UserList />
          </main>
        </ProtectedRoute>
      } />

      <Route path="/admin/learning-documentation/:userId" element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <AdminLearningDocumentation />
          </main>
        </ProtectedRoute>
      } />

      <Route path="/profile" element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <Profile />
          </main>
        </ProtectedRoute>
      } />

      <Route path="/changelog" element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <Changelog />
          </main>
        </ProtectedRoute>
      } />

      <Route path="/manage/classes" element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <SchoolClassManagement />
          </main>
        </ProtectedRoute>
      } />

      <Route path="/manage/classes/:id" element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <ClassDetails />
          </main>
        </ProtectedRoute>
      } />


      <Route path={landingRoutes.VISIBILITY_SETTINGS} element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <VisibilitySettingsPage />
          </main>
        </ProtectedRoute>
      } />

      <Route path="/manage/subjects" element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <SubjectManagement />
          </main>
        </ProtectedRoute>
      } />

      <Route path="/admin/email-history" element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <EmailHistoryPage />
          </main>
        </ProtectedRoute>
      } />
      <Route path="/invitations" element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <InvitationsPage />
          </main>
        </ProtectedRoute>
      } />
      
      <Route path={landingRoutes.INSTRUCTOR_TIMELINE} element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <ExpertAssignmentView />
          </main>
        </ProtectedRoute>
      } />

      <Route path="/expert/planpilot" element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <PlanPilotView />
          </main>
        </ProtectedRoute>
      } />

      {/* Bonus Routes */}
      <Route path={landingRoutes.BONUS} element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <BonusLandingPage />
          </main>
        </ProtectedRoute>
      } />

      <Route path="/bonus/:groupSlug" element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <LegacyBonusGroupRedirect />
          </main>
        </ProtectedRoute>
      } />

      <Route path="/bonus/students/:studentId" element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <RequireVisibilityKey keys="VIEW_ASSIGNED_LEARNER_BONUS">
              <BonusStudentDetailPage />
            </RequireVisibilityKey>
          </main>
        </ProtectedRoute>
      } />

      <Route path="/my-bonus" element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <RequireVisibilityKey keys="VIEW_BONUS">
              <MyBonusPage />
            </RequireVisibilityKey>
          </main>
        </ProtectedRoute>
      } />

      <Route path="/learner/bonus" element={<Navigate to="/my-bonus" replace />} />

      <Route path="/contact" element={
        <ProtectedRoute>
          <Navigation />
          <main className="main-content">
            <Contact />
          </main>
        </ProtectedRoute>
      } />

      <Route path="/onboarding/profession-type" element={
        <ProtectedRoute>
          <OnboardingPage />
        </ProtectedRoute>
      } />

      {/* Redirect unknown routes */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>
  ),
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true
    }
  }
);

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <VisibilityProvider>
            <RouterProvider router={router} future={{ v7_startTransition: true }} />
          </VisibilityProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
